import React from 'react';
import { CellState, Position } from '../types/game';
import { cn } from '../lib/utils';

interface GameBoardProps {
  board: CellState[][];
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  lastMove?: Position;
}

const GameBoard: React.FC<GameBoardProps> = ({
  board,
  onCellClick,
  disabled = false,
  lastMove,
}) => {
  const isLastMove = (row: number, col: number): boolean => {
    return lastMove ? lastMove.row === row && lastMove.col === col : false;
  };

  return (
    <div className="relative max-w-[700px] max-h-[700px] mx-auto">
      {/* 棋盘背景 */}
      <div className="bg-gradient-to-br from-amber-100 to-amber-200 p-2 sm:p-4 rounded-lg shadow-lg">
        <div className="border-2 border-amber-800 bg-amber-50 p-2 sm:p-4 w-full h-full mx-auto" style={{ display: 'grid', gridTemplateColumns: 'repeat(15, 1fr)', gap: '0' }}>
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={cn(
                  "relative border border-gray-400 cursor-pointer transition-all duration-200 hover:bg-amber-200/50 box-border aspect-square",
                  {
                    "cursor-not-allowed opacity-50": disabled,
                    "ring-2 ring-red-500 ring-opacity-75 bg-red-100/50": isLastMove(rowIndex, colIndex),
                  }
                )}
                onClick={() => !disabled && onCellClick({ row: rowIndex, col: colIndex })}
              >
                {/* 棋子 */}
                {cell && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className={cn(
                        "rounded-full border-2 shadow-lg transition-all duration-300 w-3/4 h-3/4 sm:w-4/5 sm:h-4/5 max-w-[30px] max-h-[30px] sm:max-w-[40px] sm:max-h-[40px]",
                        {
                          "bg-gray-900 border-gray-700": cell === 'black',
                          "bg-white border-gray-300": cell === 'white',
                        }
                      )}
                    />
                    {/* 最后一步标记 */}
                    {isLastMove(rowIndex, colIndex) && (
                      <div className="absolute pointer-events-none top-0 right-0 sm:top-0.5 sm:right-0.5">
                        <div className="bg-red-500 rounded-full animate-pulse shadow-lg w-1/4 h-1/4 sm:w-1/3 sm:h-1/3 min-w-[6px] min-h-[6px]" />
                      </div>
                    )}
                  </div>
                )}

                {/* 星位标记 */}
                {((rowIndex === 3 || rowIndex === 7 || rowIndex === 11) &&
                  (colIndex === 3 || colIndex === 7 || colIndex === 11)) && !cell && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-amber-800 rounded-full opacity-60 w-1/4 h-1/4 sm:w-1/3 sm:h-1/3 min-w-[2px] min-h-[2px]" />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 坐标标记 */}
      <div className="hidden lg:flex absolute flex flex-col justify-between h-full -left-6 sm:-left-8 py-2 sm:py-4 top-2 sm:top-4">
        {Array.from({ length: board.length }, (_, i) => (
          <div key={i} className="text-amber-800 font-medium text-xs sm:text-xs">
            {String.fromCharCode(65 + i)}
          </div>
        ))}
      </div>

      <div className="hidden lg:flex absolute flex justify-between w-full -bottom-6 sm:-bottom-8 left-4 sm:left-6 px-2 sm:px-4">
        {Array.from({ length: board[0].length }, (_, i) => (
          <div key={i} className="text-amber-800 font-medium text-xs sm:text-xs">
            {i + 1}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameBoard;
// Fixed board grid layout